import { useCallback, useEffect, useState, useRef } from 'react';
import { SurveyFormatRef, UseActiveViewReturn } from './types';
import { findFirstFocusable, scrollIntoView } from './utils';

const useActiveView = (views: SurveyFormatRef[]): UseActiveViewReturn => {
  const [viewsState, setViewsState] = useState(views);
  const [activeView, setActiveView] = useState<SurveyFormatRef | null>(null);

  const isAnimating = useRef(false);
  /**
   * The number of scroll animations currently running.
   * This is used to prevent isAnimating being set to false during a subsequent scroll animations
   * (which causes the active view to jump back and forth).
   */
  const animationCount = useRef(0);

  useEffect(() => {
    setViewsState(views);
  }, [views]);

  const setFocus = useCallback(
    (name: string) => {
      const view = viewsState.find(v => v.name === name);
      if (view?.ref?.current) {
        const firstFocusable = findFirstFocusable(view.ref.current);
        firstFocusable?.focus();
      }
    },
    [viewsState]
  );

  const setNextActive = useCallback(() => {
    const idx = viewsState.findIndex(v => v.name === activeView?.name);
    const nextActive = viewsState[idx + 1] || null;
    if (nextActive?.ref?.current) {
      setFocus(nextActive.name);
    }
  }, [viewsState, activeView, setFocus]);

  const scrollToActive = useCallback(
    (name: string) => {
      const view = viewsState.find(v => v.name === name);
      const clickShield = view?.ref?.current;
      if (clickShield) {
        const target = (clickShield.firstElementChild ?? clickShield) as HTMLElement;
        scrollIntoView(
          target,
          () => {
            animationCount.current += 1;
            isAnimating.current = true;
          },
          () => {
            if (animationCount.current <= 1) {
              isAnimating.current = false;
            }
            animationCount.current -= 1;
          }
        );
      }
    },
    [viewsState, animationCount]
  );

  const setActive = useCallback(
    (name: string | null, direct?: boolean) => {
      if (name !== null) {
        const ref = viewsState.find(view => view.name === name) || null;
        setActiveView(ref);
        if (!direct) {
          scrollToActive(name);
        }
      }
    },
    [viewsState, scrollToActive]
  );

  return {
    active: activeView,
    setActive,
    setNextActive,
    isAnimating,
  };
};

export default useActiveView;
